// ─── Shared Types for Event Detail Report ─────────────────────────────────────

export interface VendorPaymentRow {
  id: number;
  amount: number;
  paid_at: string;
  payment_method: string;
  reference: string | null;
  notes: string | null;
}

export interface VendorDetailRow {
  id: number;
  vendor_name: string;
  vendor_type: string | null;
  description: string | null;
  quantity: number;
  /** What the client is charged for this line */
  billed: number;
  /** What the line costs us (a cancelled event: only what was already paid) */
  total_amount: number;
  /** billed − cost */
  profit: number;
  commission_amount: number;
  assigned: number;
  paid: number;
  balance: number;
  status: string;
  payments: VendorPaymentRow[];
}

export interface ServiceDetailRow {
  name: string;
  vendor_name?: string | null;
  quantity: number;
  unit_price: number;
  cost_price: number;
  revenue: number;
  cost: number;
}

export interface ClientPaymentRow {
  id: number;
  amount: number;
  payment_date: string;
  payment_method: string;
  reference_number: string | null;
  notes: string | null;
}

export interface ExpenseRow {
  title: string;
  category: string;
  amount: number;
  expense_date: string;
  paid_to: string | null;
}

export interface Financials {
  income_subtotal: number;
  discount_amount: number;
  tax_amount: number;
  net_revenue: number;
  final_revenue: number;
  service_revenue: number;
  service_cost: number;
  vendor_revenue: number;
  vendor_cost: number;
  room_revenue: number;
  adhoc_cost: number;
  total_revenue: number;
  total_cost: number;
  /** Cost of services and vendors plus the event's own expenses */
  total_expense: number;
  profit: number;
  customer: { 
    subtotal: number; 
    discount: number; 
    tax: number; 
    total: number; 
    paid: number; 
    balance: number 
  };
}

export interface EventData {
  id: number;
  name: string;
  client: string;
  venue?: string | null;
  status: string;
  start_date: string;
  end_date: string;
  guest_count: number;
  event_type: string;
}

export interface VendorTotals {
  total_assigned: number;
  total_paid: number;
  total_balance: number;
  vendor_count: number;
}

export interface ServiceTotals {
  total_revenue: number;
  total_cost: number;
  total_profit: number;
}

export interface ReportData {
  can_view_money: true;
  event: EventData;
  financials: Financials;
  vendor_detail: VendorDetailRow[];
  vendor_totals: VendorTotals;
  service_totals: ServiceTotals;
  service_detail: ServiceDetailRow[];
  client_payments: ClientPaymentRow[];
  expenses: ExpenseRow[];
  is_projected: boolean;
}

/** What roles without money access get: no revenue, cost or profit. */
export interface LimitedReportData {
  can_view_money: false;
  event: EventData;
  customer: { paid: number; balance: number };
  client_payments: ClientPaymentRow[];
  vendors: { id: number; vendor_name: string; vendor_type: string | null; description: string | null; quantity: number; unit: string | null }[];
  services: { name: string; quantity: number; vendor_name: string | null }[];
}

export type AnyReportData = ReportData | LimitedReportData;

// ─── Helper Functions ─────────────────────────────────────────────────────────

export const fmt = (n: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n ?? 0);

export { formatDate as fmtDate } from "@/lib/dateFormat";

export const statusColors: Record<string, string> = {
  confirmed: "bg-emerald-100 text-emerald-700",
  draft: "bg-stone-100 text-stone-600",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-violet-100 text-violet-700",
  cancelled: "bg-rose-100 text-rose-700",
  projected: "bg-amber-100 text-amber-700",
};
